/* ==========================================================================
   theme.js — Core interactions
   - Mobile nav drawer
   - Quantity input
   - Add-to-cart (AJAX, Shopify cart API)
   - Cart count refresh
   ========================================================================== */
(function () {
  'use strict';

  // ---- Mobile menu drawer ----
  const drawer = document.querySelector('[data-menu-drawer]');
  const drawerOpen = document.querySelector('[data-menu-open]');
  const drawerClose = document.querySelector('[data-menu-close]');

  function setDrawer(open) {
    if (!drawer) return;
    drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.body.style.overflow = open ? 'hidden' : '';
    if (open) {
      // Move focus to close button for keyboard users
      requestAnimationFrame(() => drawerClose && drawerClose.focus());
    } else {
      drawerOpen && drawerOpen.focus();
    }
  }
  drawerOpen && drawerOpen.addEventListener('click', () => setDrawer(true));
  drawerClose && drawerClose.addEventListener('click', () => setDrawer(false));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer && drawer.getAttribute('aria-hidden') === 'false') {
      setDrawer(false);
    }
  });

  // ---- Quantity input ----
  document.querySelectorAll('[data-qty]').forEach((wrap) => {
    const input = wrap.querySelector('input[type="number"]');
    if (!input) return;
    wrap.querySelectorAll('[data-qty-step]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const step = parseInt(btn.dataset.qtyStep, 10) || 1;
        const next = Math.max(1, (parseInt(input.value, 10) || 1) + step);
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });

  // ---- Money formatter — handles all Shopify money_format tokens ----
  // Supports: {{amount}}, {{amount_no_decimals}}, {{amount_with_comma_separator}},
  //           {{amount_no_decimals_with_comma_separator}}, {{amount_with_apostrophe_separator}}
  function withComma(n) {
    // Indian numbering: 1,00,000 (lakh) format if INR; else western 1,000,000
    const isINR = ((window.themeSettings && window.themeSettings.shopCurrency) || 'INR') === 'INR';
    const parts = String(n).split('.');
    const intPart = parts[0];
    let formatted;
    if (isINR) {
      // 1234567 → 12,34,567
      const last3 = intPart.slice(-3);
      const rest = intPart.slice(0, -3);
      formatted = rest ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3 : last3;
    } else {
      formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return parts.length > 1 ? formatted + '.' + parts[1] : formatted;
  }

  function formatMoney(cents) {
    const value = cents / 100;
    const amount = value.toFixed(2);
    const amountNoDec = Math.round(value).toString();
    const amountComma = withComma(amount);
    const amountNoDecComma = withComma(amountNoDec);
    const amountApos = amount.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
    const format = (window.themeSettings && window.themeSettings.moneyFormat) || '₹{{amount_no_decimals_with_comma_separator}}';
    return format
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecComma)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amountComma)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/g, amountApos)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDec)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  }
  window.formatMoney = formatMoney;

  // ---- Refresh cart count from Shopify /cart.js ----
  async function refreshCartCount() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) return;
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.hidden = cart.item_count === 0;
      });
    } catch (_) { /* network errors silent */ }
  }
  window.refreshCartCount = refreshCartCount;

  // ---- Add to cart (delegated) ----
  document.addEventListener('submit', async (e) => {
    const form = e.target.closest('form[action$="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    const original = btn ? btn.textContent : '';
    if (btn) { btn.setAttribute('aria-disabled', 'true'); btn.textContent = 'Adding…'; }
    try {
      const formData = new FormData(form);
      const res = await fetch(window.themeSettings.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Accept': 'application/javascript' },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'Could not add to cart.');
      if (btn) btn.textContent = 'Added ✓';
      await refreshCartCount();
      setTimeout(() => { if (btn) btn.textContent = original; }, 1600);
    } catch (err) {
      console.error(err);
      if (btn) btn.textContent = 'Try again';
      setTimeout(() => { if (btn) btn.textContent = original; }, 1600);
    } finally {
      if (btn) btn.removeAttribute('aria-disabled');
    }
  });

  // ---- Initial cart count ----
  refreshCartCount();
})();
